import { expVal } from '@atlassian/jira-feature-experiments';
import type { UseResourceHookResponse } from '@atlassian/react-resource-router';

export type Trait = {
	name: string;
	value: string | boolean | number;
};

export type PartialResource<T> = Pick<UseResourceHookResponse<T>, 'loading' | 'error' | 'data'>;

const validateTrait = (v: Trait | unknown): v is Trait =>
	v !== null &&
	typeof v === 'object' &&
	'name' in v &&
	typeof v.name === 'string' &&
	'value' in v &&
	['string', 'boolean', 'number'].includes(typeof v.value);

/**
 * For route resources that fetch TAP traits combine any number of separate useResource values into a single resource-like value of flat traits
 */
export const mergeTraitsResources = (
	...traitResources: ReadonlyArray<PartialResource<(Trait | null)[] | null>>
): PartialResource<Trait[]> => {
	const loading = traitResources.some(({ loading: l }) => l);
	const { error } = traitResources.find(({ error: e }) => e) ?? { error: null };
	if (error) {
		return { loading, error, data: null };
	}

	const data = traitResources.map(({ data: d }) => d);
	if (!data.every(Boolean)) {
		return { loading, error: null, data: null };
	}

	return {
		loading,
		error: null,
		data: data.flatMap((v) => v).filter(Boolean),
	};
};

/**
 * Get a useResource like value that represents TAP traits.
 *
 * The TAP traits returned are those defined in a permanent Statsig dynamic config "endeavour_dashboards_traits_override".
 * This allows us to test traits in deployed environments.
 */
export const getTraitsResourceFromStatsigConfig = (): PartialResource<Trait[]> => {
	const getDynamicConfig = expVal<string[]>;
	const traitsOrErrors = getDynamicConfig('endeavour_dashboards_traits_override', 'value', []).map(
		(json: string, i: number): Trait | Error => {
			try {
				const maybeTrait = JSON.parse(json);
				const isValid = validateTrait(maybeTrait);
				return isValid
					? maybeTrait
					: new Error(
							`Dynamic config "endeavour_dashboards_traits_override" element [${i}] is misconfigured`,
						);
			} catch (e) {
				return new Error(
					`Badly formed JSON in dynamic config "endeavour_dashboards_traits_override" element [${i}]`,
				);
			}
		},
	);

	const error = traitsOrErrors.find((v) => v instanceof Error) ?? null;
	const data = error ? null : traitsOrErrors.filter(validateTrait);

	return { loading: false, error, data };
};
